import { useEffect, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/lib/analytics";
import { btnLift, focusRing, focusRingDark } from "@/components/home/styles";
import { APP_STORE_URL, PLAY_STORE_URL, withUtm } from "@/constants/storeLinks";

interface AppStoreBadgesProps {
  size?: "sm" | "md" | "lg";
  /**
   * dark / light: classic rounded badges.
   * pill: Clarity v2 pills on light surfaces (ink App Store + outlined Google Play).
   * pill-inverse: Clarity v2 pills on the deep-forest band.
   * maison: square, hairline buttons for the /lp/maison experiment (ivory surfaces).
   */
  variant?: "dark" | "light" | "pill" | "pill-inverse" | "maison";
  className?: string;
  align?: "start" | "center";
  location?: string;
  /** Adds utm_source=<location>, utm_medium=web, utm_campaign=<utmCampaign> */
  utmCampaign?: string;
}

const AppStoreBadges = ({
  size = "md",
  variant = "dark",
  className = "",
  align = "start",
  location = "unknown",
  utmCampaign,
}: AppStoreBadgesProps) => {
  const { t } = useTranslation();

  if (variant === "maison") {
    return (
      <MaisonButtons
        className={className}
        align={align}
        location={location}
        utmCampaign={utmCampaign}
        downloadOn={t("appStore.downloadOn")}
        getItOn={t("appStore.getItOn")}
      />
    );
  }

  if (variant === "pill" || variant === "pill-inverse") {
    return (
      <StorePills
        inverse={variant === "pill-inverse"}
        className={className}
        align={align}
        location={location}
        utmCampaign={utmCampaign}
        downloadOn={t("appStore.downloadOn")}
        getItOn={t("appStore.getItOn")}
      />
    );
  }

  const heights = { sm: "h-11", md: "h-14", lg: "h-16" };
  const padX = { sm: "px-4", md: "px-5", lg: "px-6" };
  const iconSize = { sm: "w-6 h-6", md: "w-7 h-7", lg: "w-8 h-8" };
  const subText = { sm: "text-[9px]", md: "text-[10px]", lg: "text-xs" };
  const mainText = { sm: "text-sm", md: "text-base", lg: "text-lg" };

  const appStoreHref = withUtm(APP_STORE_URL, location, utmCampaign);
  const playStoreHref = withUtm(PLAY_STORE_URL, location, utmCampaign);

  const isDark = variant === "dark";
  const base = isDark
    ? "bg-foreground text-background hover:bg-foreground/90 border-foreground/20"
    : "bg-background text-foreground hover:bg-background/90 border-border";

  return (
    <div
      className={`flex flex-col sm:flex-row gap-3 ${
        align === "center" ? "items-center justify-center" : "items-start"
      } ${className}`}
    >
      {/* App Store */}
      <motion.a
        href={appStoreHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("store_click", { store: "app_store", location })}
        whileHover={{ y: -2, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`group flex items-center gap-3 ${heights[size]} ${padX[size]} rounded-2xl border ${base} shadow-lg transition-all`}
        aria-label="Download on the App Store"
      >
        <svg viewBox="0 0 384 512" className={iconSize[size]} fill="currentColor">
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
        </svg>
        <div className="flex flex-col leading-tight">
          <span className={`${subText[size]} opacity-80`}>{t("appStore.downloadOn")}</span>
          <span className={`${mainText[size]} font-semibold tracking-tight`}>
            App Store
          </span>
        </div>
      </motion.a>

      {/* Google Play */}
      <motion.a
        href={playStoreHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("store_click", { store: "google_play", location })}
        whileHover={{ y: -2, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`group flex items-center gap-3 ${heights[size]} ${padX[size]} rounded-2xl border ${base} shadow-lg transition-all`}
        aria-label="Get it on Google Play"
      >
        <svg viewBox="0 0 512 512" className={iconSize[size]}>
          <defs>
            <linearGradient id="gp1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00C4FF" />
              <stop offset="100%" stopColor="#0078FF" />
            </linearGradient>
            <linearGradient id="gp2" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFD600" />
              <stop offset="100%" stopColor="#FF8A00" />
            </linearGradient>
            <linearGradient id="gp3" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF3A44" />
              <stop offset="100%" stopColor="#C31162" />
            </linearGradient>
            <linearGradient id="gp4" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00F076" />
              <stop offset="100%" stopColor="#00A65A" />
            </linearGradient>
          </defs>
          <path
            d="M76 32c-7 4-11 12-11 22v404c0 10 4 18 11 22l232-224z"
            fill="url(#gp1)"
          />
          <path d="M308 256l68-66-232-134c-8-4-15-4-21-1z" fill="url(#gp4)" />
          <path d="M308 256l-185 201c6 3 13 3 21-1l232-134z" fill="url(#gp3)" />
          <path
            d="M376 322l71-41c14-8 14-30 0-38l-71-41-68 54z"
            fill="url(#gp2)"
          />
        </svg>
        <div className="flex flex-col leading-tight">
          <span className={`${subText[size]} opacity-80`}>{t("appStore.getItOn")}</span>
          <span className={`${mainText[size]} font-semibold tracking-tight`}>
            Google Play
          </span>
        </div>
      </motion.a>
    </div>
  );
};

type Store = "app_store" | "google_play";

const AppleGlyph = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.97-3.46-1.07-1.57-2.74-1.78-3.33-1.8-1.42-.14-2.77.83-3.49.83-.72 0-1.83-.81-3.01-.79-1.55.02-2.98.9-3.78 2.29-1.61 2.8-.41 6.94 1.16 9.21.77 1.11 1.68 2.36 2.88 2.31 1.16-.05 1.59-.75 2.99-.75 1.39 0 1.79.75 3.01.72 1.24-.02 2.03-1.13 2.79-2.25.88-1.29 1.24-2.54 1.26-2.6-.03-.01-2.42-.93-2.45-3.71zM14.08 5.84c.64-.77 1.07-1.85.95-2.92-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.78-.97 2.83 1.02.08 2.07-.52 2.71-1.29z" />
  </svg>
);

const PlayGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
    <path d="M5 3.6v16.8c0 .6.6.9 1.1.6l14.3-8.4c.5-.3.5-1 0-1.3L6.1 3C5.6 2.7 5 3 5 3.6z" />
    <path d="M5.4 3.2 15 12l-9.6 8.8" />
  </svg>
);

interface StorePillsProps {
  inverse: boolean;
  className: string;
  align: "start" | "center";
  location: string;
  utmCampaign?: string;
  downloadOn: string;
  getItOn: string;
}

/** Clarity v2 store pills (56px, fully rounded). Same URLs, UTM and tracking as the badges. */
const StorePills = ({ inverse, className, align, location, utmCampaign, downloadOn, getItOn }: StorePillsProps) => {
  const base = `inline-flex h-14 items-center gap-2.5 rounded-full pe-6 ps-[18px] no-underline ${btnLift} ${inverse ? focusRingDark : focusRing}`;
  const apple = inverse ? "bg-white text-foreground" : "bg-foreground text-white";
  const play = inverse
    ? "border border-white/70 bg-transparent text-white"
    : "border border-foreground bg-white text-foreground";
  const label = (sub: string, main: string) => (
    <span className="flex flex-col items-start leading-[1.1]">
      <span className="text-[11px] font-medium">{sub}</span>
      <span className="text-lg font-semibold tracking-[-0.01em]">{main}</span>
    </span>
  );
  return (
    <div className={`flex flex-wrap items-center gap-3.5 ${align === "center" ? "justify-center" : "justify-start"} ${className}`}>
      <a
        href={withUtm(APP_STORE_URL, location, utmCampaign)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("store_click", { store: "app_store", location })}
        className={`${base} ${apple}`}
        aria-label="Download on the App Store"
      >
        <AppleGlyph />
        {label(downloadOn, "App Store")}
      </a>
      <a
        href={withUtm(PLAY_STORE_URL, location, utmCampaign)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("store_click", { store: "google_play", location })}
        className={`${base} ${play}`}
        aria-label="Get it on Google Play"
      >
        <PlayGlyph />
        {label(getItOn, "Google Play")}
      </a>
    </div>
  );
};

type MaisonButtonsProps = Omit<StorePillsProps, "inverse">;

/** Maison store buttons (56px, 2px radius, forest ink on ivory). Same URLs, UTM and tracking as the badges. */
const MaisonButtons = ({ className, align, location, utmCampaign, downloadOn, getItOn }: MaisonButtonsProps) => {
  const base =
    "mz-btn inline-flex min-h-14 items-center justify-center gap-3 rounded-[2px] border border-[#0E2A22] px-[26px] text-start no-underline";
  const label = (sub: string, main: string) => (
    <span className="flex flex-col leading-[1.15]">
      <span className="text-[10px] uppercase tracking-[0.14em] opacity-85">{sub}</span>
      <span className="text-base font-medium tracking-[0.01em]">{main}</span>
    </span>
  );
  return (
    <div
      className={`flex flex-col items-stretch gap-3 sm:flex-row sm:items-center ${
        align === "center" ? "sm:justify-center" : "sm:justify-start"
      } ${className}`}
    >
      <a
        href={withUtm(APP_STORE_URL, location, utmCampaign)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("store_click", { store: "app_store", location })}
        className={`${base} mz-btn-solid bg-[#0E2A22] text-[#F7F4EE]`}
        aria-label="Download on the App Store"
      >
        <AppleGlyph />
        {label(downloadOn, "App Store")}
      </a>
      <a
        href={withUtm(PLAY_STORE_URL, location, utmCampaign)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent("store_click", { store: "google_play", location })}
        className={`${base} mz-btn-line bg-transparent text-[#0E2A22]`}
        aria-label="Get it on Google Play"
      >
        <PlayGlyph />
        {label(getItOn, "Google Play")}
      </a>
    </div>
  );
};

/** App Store by default; Google Play on Android devices (detected after mount). */
const useDetectedStore = (): Store => {
  const [store, setStore] = useState<Store>("app_store");
  useEffect(() => {
    if (typeof navigator !== "undefined" && /android/i.test(navigator.userAgent)) {
      setStore("google_play");
    }
  }, []);
  return store;
};

interface SmartStoreLinkProps {
  location: string;
  utmCampaign?: string;
  className?: string;
  /** Render prop so the label can change for Android visitors. */
  children: (store: Store) => ReactNode;
}

/** One store link that points to the visitor's platform store, with the same UTM + `store_click` tracking. */
export const SmartStoreLink = ({ location, utmCampaign, className = "", children }: SmartStoreLinkProps) => {
  const store = useDetectedStore();
  const href = withUtm(store === "google_play" ? PLAY_STORE_URL : APP_STORE_URL, location, utmCampaign);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("store_click", { store, location })}
      className={className}
    >
      {children(store)}
    </a>
  );
};

export default AppStoreBadges;
